import { UiState } from 'instantsearch.js/es/types/ui-state'
import { omit } from 'lodash'
import QueryString from 'qs'

import useAiSearchStore from '@/stores/aiSearchStore'
import { applySearchTypeAfterUrlParse } from '@/stores/searchSelectorStore'

const CUSTOM_URL_PARAMS = ['searchByAudio', 'searchByPrompt']

export const createSearchUrlWithCustomParams = ({
	qsModule,
	location,
	routeState,
	user,
}: {
	qsModule: typeof QueryString
	location: { origin: string; pathname: string; hash: string; search: string }
	routeState: UiState
	user: User
}): string => {
	const { origin, pathname, hash } = location

	// Drive AI params from store only. `location.search` can lag behind Zustand after
	// clearAiSearch + refine(); merging from URL would re-attach searchByPrompt/searchByAudio.
	const {
		searchByAudio: storeSearchByAudio,
		searchByPrompt: storeSearchByPrompt,
	} = useAiSearchStore.getState()

	const allowedParams: Record<string, string> = {}
	if (user && storeSearchByAudio?.url) {
		allowedParams.searchByAudio = storeSearchByAudio.url
	}
	if (user && storeSearchByPrompt) {
		allowedParams.searchByPrompt = storeSearchByPrompt
	}

	// merge algolia routeState with allowedParams
	const mergedState = { ...routeState, ...allowedParams }

	const queryString = qsModule.stringify(mergedState)

	if (!queryString) {
		return `${origin}${pathname}${hash}`
	}

	return `${origin}${pathname}?${queryString}${hash}`
}

export const parseSearchUrlWithCustomParams = ({
	qsModule,
	location,
	user,
}: {
	qsModule: typeof QueryString
	location: { origin: string; pathname: string; hash: string; search: string }
	user: User
}): UiState => {
	const { search } = location

	const parsed = qsModule.parse(search.slice(1))

	const {
		searchByAudio: currentSearchByAudio,
		searchByPrompt: currentSearchByPrompt,
		clearAiSearch,
		setSearchByAudio,
		setSearchByPrompt,
		prefetchSearchByAudio,
	} = useAiSearchStore.getState()

	// Sync searchByAudio from URL to store (only if different)
	if (
		parsed.searchByAudio &&
		typeof parsed.searchByAudio === 'string' &&
		currentSearchByAudio?.url !== parsed.searchByAudio
	) {
		prefetchSearchByAudio(parsed.searchByAudio, !!user)
		setSearchByAudio(parsed.searchByAudio)
	}

	// Sync searchByPrompt from URL to store (only if logged in and different)
	if (
		parsed.searchByPrompt &&
		typeof parsed.searchByPrompt === 'string' &&
		user &&
		currentSearchByPrompt !== parsed.searchByPrompt
	) {
		setSearchByPrompt(parsed.searchByPrompt)
	}

	let clearedAiStateFromUrl = false

	// Clear store when searchByPrompt is removed from URL (browser back/forward)
	if (!parsed.searchByPrompt && currentSearchByPrompt) {
		clearedAiStateFromUrl = true
		clearAiSearch()
	}

	// Clear store when searchByAudio is removed from URL (browser back/forward)
	if (!parsed.searchByAudio && currentSearchByAudio) {
		clearedAiStateFromUrl = true
		clearAiSearch()
	}

	applySearchTypeAfterUrlParse({
		user,
		parsed,
		clearedAiStateFromUrl,
	})

	// Strip custom params and return only Algolia state for InstantSearch
	return omit(parsed, CUSTOM_URL_PARAMS) as UiState
}
